/**
 * Aussendungen: Übersicht und geführter Ablauf.
 *
 * Der Ablauf ist bewusst in vier Schritte zerlegt, die jeweils genau eine
 * Entscheidung verlangen. Wer das Werkzeug zum ersten Mal benutzt, wird
 * durchgeführt; wer es kennt, klickt dieselben vier Knöpfe schneller.
 *
 * Schritt 3 ist der wichtigste und lässt sich nicht überspringen: dort sieht
 * man die fertige Nachricht mit eingesetzten Werten, die Prüfbefunde und die
 * ehrliche Angabe, wie lange der Versand dauert und wie viele Empfänger nur
 * die neutrale Anrede bekommen.
 */

import { Kampagnen, Vorlagen, Segmente } from '../api.js';
import {
  el,
  ersetzen,
  karte,
  knopf,
  kennzeichen,
  meldung,
  eingabefeld,
  schrittanzeige,
  laedt,
  zeitpunkt,
  dialog,
} from '../ui.js';

const SCHRITTE = [
  { titel: 'Vorlage' },
  { titel: 'Empfänger' },
  { titel: 'Prüfen' },
  { titel: 'Senden' },
];

/** Für diese Zustände werden die Kennzahlen der Aussendung nachgeladen. */
const KENNZAHLEN_STATUS = new Set(['fertig', 'angehalten', 'welle_beendet']);

// Schwellen wie in server/src/kampagnen/kennzahlen.js — hier nur zur
// farblichen Einordnung, die Zahlen selbst kommen vom Server.
const MINDEST_VERSENDET = 100;
const BESCHWERDE_WARNUNG = 0.001;
const BESCHWERDE_KRITISCH = 0.003;
const UNZUSTELLBAR_WARNUNG = 0.02;

/** @param {number} wert Anteil von 0 bis 1. */
function formatProzent(wert) {
  return wert.toLocaleString('de-AT', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * @param {string} label
 * @param {string} text
 * @returns {HTMLElement}
 */
function kennwert(label, text) {
  return el('div.kennwert', [
    el('span.kennwert-zahl', { text }),
    el('span.kennwert-label', { text: label }),
  ]);
}

/**
 * @param {string} label
 * @param {string} text
 * @param {'gruen'|'gelb'|'rot'} farbe
 * @returns {HTMLElement}
 */
function kennwertQuote(label, text, farbe) {
  return el('div.kennwert', [kennzeichen(text, farbe), el('span.kennwert-label', { text: label })]);
}

/**
 * Baut den Inhalt der nachgeladenen Kennzahlen.
 *
 * Unter 100 versendeten Nachrichten ist jede Quote Zufall — eine einzige
 * Beschwerde ergäbe rechnerisch schon einen Ausreißer. Deshalb wird dann
 * keine Farbe gezeigt, sondern gesagt, warum.
 *
 * @param {any} zahlen
 * @returns {Node[]}
 */
function kennzahlenInhalt(zahlen) {
  /** @type {Node[]} */
  const teile = [];

  if (!zahlen.ruecklaufEingerichtet) {
    teile.push(
      meldung(
        'warnung',
        'Der Rücklaufabruf ist nicht eingerichtet: Unzustellbarkeiten und Beschwerden, die erst nach ' +
          'dem Versand eintreffen, fehlen hier noch. Die tatsächlichen Werte liegen daher höher als angezeigt.',
      ),
    );
  }

  teile.push(
    el('div.kennzahlen-leiste', [
      kennwert('Versendet', String(zahlen.versendet)),
      kennwert('Unzustellbar', String(zahlen.unzustellbar)),
      kennwert('Beschwerden', String(zahlen.beschwerden)),
      kennwert('Abgemeldet', String(zahlen.abmeldungen)),
    ]),
  );

  if (zahlen.versendet < MINDEST_VERSENDET) {
    teile.push(
      meldung(
        'info',
        `Erst ab ${MINDEST_VERSENDET} versendeten Nachrichten sind Beschwerde- und Unzustellbarquote ` +
          `aussagekräftig — bisher ${zahlen.versendet}. Bis dahin entscheidet der Zufall über jede Quote.`,
      ),
    );
  } else {
    teile.push(
      el('div.kennzahlen-leiste', [
        kennwertQuote(
          'Beschwerdequote',
          formatProzent(zahlen.beschwerdequote),
          zahlen.beschwerdequote >= BESCHWERDE_KRITISCH
            ? 'rot'
            : zahlen.beschwerdequote >= BESCHWERDE_WARNUNG
              ? 'gelb'
              : 'gruen',
        ),
        kennwertQuote(
          'Unzustellbarquote',
          formatProzent(zahlen.unzustellbarquote),
          zahlen.unzustellbarquote >= UNZUSTELLBAR_WARNUNG ? 'gelb' : 'gruen',
        ),
      ]),
    );
  }

  if (zahlen.befunde?.length) {
    teile.push(befundeListe(zahlen.befunde));
  }

  return teile;
}

/**
 * Lädt die Kennzahlen einer Aussendung nach und zeigt sie an.
 * @param {any} kampagne
 * @returns {HTMLElement}
 */
function kennzahlenBereich(kampagne) {
  const bereich = el('div.kennzahlen-block', [laedt('Kennzahlen werden geladen …')]);
  Kampagnen.kennzahlen(kampagne.id)
    .then((zahlen) => ersetzen(bereich, ...kennzahlenInhalt(zahlen)))
    .catch((fehler) =>
      ersetzen(
        bereich,
        meldung(
          'warnung',
          `Kennzahlen konnten nicht geladen werden: ${
            fehler instanceof Error ? fehler.message : 'unbekannter Fehler'
          }`,
        ),
      ),
    );
  return bereich;
}

/**
 * Nach einer beendeten Welle: wie viele stehen noch aus, und wie es
 * weitergeht. Eine beendete Welle ist kein Fehler — sie ist der geplante
 * Halt nach der vereinbarten Menge.
 *
 * @param {any} kampagne
 * @param {() => void} neuLaden
 * @returns {HTMLElement}
 */
function welleFortsetzenBereich(kampagne, neuLaden) {
  const fehlerBereich = el('div');
  const weiterKnopf = knopf('Verbleibende senden', {
    art: 'haupt',
    beiKlick: async () => {
      weiterKnopf.disabled = true;
      ersetzen(fehlerBereich, laedt('Wird fortgesetzt …'));
      try {
        await Kampagnen.senden(kampagne.id);
        neuLaden();
      } catch (fehler) {
        weiterKnopf.disabled = false;
        ersetzen(
          fehlerBereich,
          meldung('fehler', fehler instanceof Error ? fehler.message : 'Fortsetzen fehlgeschlagen.'),
        );
      }
    },
  });
  return el('div', [
    meldung(
      'info',
      `Die erste Welle ist beendet: ${kampagne.gesendet} versendet, ${kampagne.offen} noch offen. ` +
        'Fortsetzen sendet an alle verbleibenden Empfänger ohne weitere Begrenzung.',
      weiterKnopf,
    ),
    fehlerBereich,
  ]);
}

/**
 * @param {any} kampagne
 * @returns {HTMLElement}
 */
function zustand(kampagne) {
  const zuordnung = {
    entwurf: /** @type {const} */ (['Entwurf', 'neutral']),
    laeuft: /** @type {const} */ (['Läuft', 'blau']),
    fertig: /** @type {const} */ (['Versendet', 'gruen']),
    angehalten: /** @type {const} */ (['Angehalten', 'gelb']),
    welle_beendet: /** @type {const} */ (['Welle beendet', 'gelb']),
    fehler: /** @type {const} */ (['Fehler', 'rot']),
  };
  const [text, farbe] = zuordnung[/** @type {keyof typeof zuordnung} */ (kampagne.status)] ?? [
    kampagne.status,
    'neutral',
  ];
  return kennzeichen(text, farbe);
}

/**
 * Der Abbruchgrund einer fehlgeschlagenen Aussendung — nur ein Text, kein
 * Zähler. Schützt gegen den Fall, dass die Spalte einmal etwas anderes als
 * Text liefert: dann lieber gar nichts anzeigen als eine sinnlose Zahl.
 *
 * @param {any} kampagne
 * @returns {string|null}
 */
function abbruchgrund(kampagne) {
  return typeof kampagne.fehler === 'string' && kampagne.fehler.trim() ? kampagne.fehler.trim() : null;
}

/**
 * Inhalt der Sende-Rückfrage: Empfängerzahl und Dauer, damit sich niemand
 * versehentlich einen Versand auslöst, der sich nicht zurückholen lässt.
 * Wird sowohl in einem eigenen Dialog (Fortsetzen) als auch eingebettet in
 * einen bereits offenen Dialog (Prüfen und senden) verwendet.
 *
 * @param {object} optionen
 * @param {number} optionen.empfaenger
 * @param {string} optionen.geschaetzteDauer
 * @param {string} [optionen.text]
 * @param {() => Promise<any>} optionen.senden
 * @param {() => void} optionen.abbrechen
 * @param {() => void} optionen.nachErfolg
 * @returns {HTMLElement}
 */
function bestaetigungsInhalt({ empfaenger, geschaetzteDauer, text, senden, abbrechen, nachErfolg }) {
  const fehlerBereich = el('div');
  const sendenKnopf = knopf('Jetzt senden', {
    art: 'haupt',
    beiKlick: async () => {
      sendenKnopf.disabled = true;
      abbrechenKnopf.disabled = true;
      try {
        await senden();
        nachErfolg();
      } catch (fehler) {
        sendenKnopf.disabled = false;
        abbrechenKnopf.disabled = false;
        ersetzen(
          fehlerBereich,
          meldung('fehler', fehler instanceof Error ? fehler.message : 'Versand fehlgeschlagen.'),
        );
      }
    },
  });
  const abbrechenKnopf = knopf('Abbrechen', { beiKlick: abbrechen });

  return el('div', [
    el('p', {
      text:
        text ?? `Die Nachricht geht an ${empfaenger} Empfänger. Das lässt sich nicht rückgängig machen.`,
    }),
    el('p', {
      text: `Voraussichtliche Dauer: ${geschaetzteDauer}. Sie können den Versand jederzeit anhalten.`,
    }),
    el('div.knopf-reihe', [abbrechenKnopf, sendenKnopf]),
    fehlerBereich,
  ]);
}

/**
 * Nach einem Anhalten mitten im Versand: wie viele Empfänger noch offen
 * sind, und wie es weitergeht. Anders als eine beendete Welle ist das kein
 * geplanter Halt — jemand hat bewusst angehalten und entscheidet jetzt, ob
 * und wann es weitergeht.
 *
 * @param {any} kampagne
 * @param {() => void} neuLaden
 * @returns {HTMLElement}
 */
function angehaltenFortsetzenBereich(kampagne, neuLaden) {
  const ergebnisBereich = el('div');
  const fortsetzenKnopf = knopf('Fortsetzen', {
    art: 'haupt',
    beiKlick: async () => {
      fortsetzenKnopf.disabled = true;
      ersetzen(ergebnisBereich, laedt('Angaben werden geladen …'));
      try {
        const vorschau = await Kampagnen.vorschau(kampagne.id);
        ersetzen(ergebnisBereich);
        const schliessen = dialog({
          titel: 'Aussendung fortsetzen?',
          inhalt: [
            bestaetigungsInhalt({
              empfaenger: vorschau.empfaenger,
              geschaetzteDauer: vorschau.geschaetzteDauer,
              text: `Die verbleibenden ${vorschau.empfaenger} Empfänger werden jetzt angeschrieben. Das lässt sich nicht rückgängig machen.`,
              senden: () => Kampagnen.senden(kampagne.id),
              abbrechen: () => schliessen(),
              nachErfolg: () => {
                schliessen();
                neuLaden();
              },
            }),
          ],
        });
      } catch (fehler) {
        ersetzen(
          ergebnisBereich,
          meldung(
            'fehler',
            fehler instanceof Error ? fehler.message : 'Angaben konnten nicht geladen werden.',
          ),
        );
      } finally {
        fortsetzenKnopf.disabled = false;
      }
    },
  });
  return el('div', [
    meldung(
      'info',
      `Der Versand wurde angehalten: ${kampagne.gesendet} versendet, ${kampagne.offen} noch offen. ` +
        'Sie können jederzeit fortsetzen.',
      fortsetzenKnopf,
    ),
    ergebnisBereich,
  ]);
}

/**
 * Schmaler Fortschrittsbalken für eine laufende Aussendung.
 * @param {any} kampagne
 * @returns {HTMLElement}
 */
function fortschrittsleiste(kampagne) {
  const gesamt = kampagne.gesendet + kampagne.offen + kampagne.fehlgeschlagen;
  const anteil = gesamt > 0 ? Math.round((kampagne.gesendet / gesamt) * 100) : 0;
  return el('div.fortschritt-leiste', [
    el('div.fortschritt-fuellung', { style: `width: ${anteil}%` }),
  ]);
}

/**
 * Aufklappbare Detailansicht: Empfängerstand (inklusive "unklar"), Vorlage,
 * Betreff und Zeitpunkte. Lädt erst beim Öffnen nach — die Liste selbst
 * kennt die genaue Aufschlüsselung nicht.
 *
 * @param {any} kampagne
 * @returns {HTMLElement}
 */
function detailsBereich(kampagne) {
  const inhaltBereich = el('div', [laedt('Details werden geladen …')]);
  let geladen = false;

  const details = /** @type {HTMLDetailsElement} */ (
    el(
      'details.vertiefung',
      {
        ontoggle: async () => {
          if (!details.open || geladen) return;
          geladen = true;
          try {
            const voll = await Kampagnen.holen(kampagne.id);
            const grund = abbruchgrund(voll);
            ersetzen(
              inhaltBereich,
              el('dl.kennwerte', [
                el('div', [el('dt', { text: 'Vorlage' }), el('dd', { text: voll.vorlage_name })]),
                el('div', [el('dt', { text: 'Betreff' }), el('dd', { text: voll.betreff })]),
                el('div', [el('dt', { text: 'Angelegt' }), el('dd', { text: zeitpunkt(voll.erstellt_am) })]),
                el('div', [el('dt', { text: 'Gestartet' }), el('dd', { text: zeitpunkt(voll.gestartet_am) })]),
                el('div', [el('dt', { text: 'Beendet' }), el('dd', { text: zeitpunkt(voll.beendet_am) })]),
              ]),
              el('div.kacheln', [
                el('div.kachel', [
                  el('div.zahl', { text: String(voll.gesendet) }),
                  el('div.beschriftung', { text: 'Versendet' }),
                ]),
                el('div.kachel', [
                  el('div.zahl', { text: String(voll.offen) }),
                  el('div.beschriftung', { text: 'Offen' }),
                ]),
                el('div.kachel', [
                  el('div.zahl', { text: String(voll.fehlgeschlagen) }),
                  el('div.beschriftung', { text: 'Fehlgeschlagen' }),
                ]),
                el('div.kachel', [
                  el('div.zahl', { text: String(voll.unklar) }),
                  el('div.beschriftung', { text: 'Unklar' }),
                ]),
              ]),
              voll.unklar > 0
                ? meldung(
                    'info',
                    'Unklar: Bei diesen Empfängern kam es mitten im Versand zu einem Abbruch, und es ' +
                      'lässt sich nicht mehr feststellen, ob die Nachricht angekommen ist. Sie werden ' +
                      'absichtlich NICHT automatisch erneut angeschrieben — lieber eine fehlende als eine ' +
                      'doppelte Nachricht an eine Mandantin oder einen Mandanten.',
                  )
                : null,
              grund ? meldung('fehler', `Abbruchgrund: ${grund}`) : null,
            );
          } catch (fehler) {
            ersetzen(
              inhaltBereich,
              meldung(
                'fehler',
                fehler instanceof Error ? fehler.message : 'Details konnten nicht geladen werden.',
              ),
            );
          }
        },
      },
      [el('summary', { text: 'Details' }), inhaltBereich],
    )
  );
  return details;
}

/**
 * Öffnet die Prüfung für einen Entwurf direkt aus der Liste — derselbe Blick
 * auf die fertige Nachricht wie im geführten Ablauf, nur ohne die
 * vorausgehenden Schritte, die für einen bestehenden Entwurf schon erledigt
 * sind.
 *
 * @param {any} kampagne
 * @param {() => void} neuLaden
 */
function pruefenUndSendenDialog(kampagne, neuLaden) {
  const inhaltBereich = el('div', [laedt('Nachricht wird zusammengesetzt …')]);
  const schliessen = dialog({
    titel: `Prüfen und senden — ${kampagne.name}`,
    breit: true,
    inhalt: [inhaltBereich],
  });

  /** @param {any} vorschau */
  function uebersichtZeichnen(vorschau) {
    const fehlerListe = vorschau.befunde.filter((/** @type {any} */ b) => b.schwere === 'fehler');
    const warnungen = vorschau.befunde.filter((/** @type {any} */ b) => b.schwere === 'warnung');

    const kopfMeldung = vorschau.blockiert
      ? meldung(
          'fehler',
          `${fehlerListe.length} Punkte verhindern den Versand. Bitte die Vorlage im Editor überarbeiten.`,
        )
      : meldung(
          'erfolg',
          'Die Nachricht ist versandfähig — sie wird in Outlook, Gmail und Apple Mail korrekt dargestellt.',
        );

    const zahlen = el('div.kacheln', [
      el('div.kachel', [
        el('div.zahl', { text: String(vorschau.empfaenger) }),
        el('div.beschriftung', { text: 'Empfänger' }),
      ]),
      el('div.kachel', [
        el('div.zahl', { text: vorschau.geschaetzteDauer }),
        el('div.beschriftung', { text: 'Voraussichtliche Dauer' }),
      ]),
      el('div.kachel', [
        el('div.zahl', { text: String(vorschau.ohnePersoenlicheAnrede) }),
        el('div.beschriftung', { text: 'ohne persönliche Anrede' }),
      ]),
    ]);

    const sendenKnopf = knopf('Jetzt senden', {
      art: 'haupt',
      deaktiviert: vorschau.blockiert || vorschau.empfaenger === 0,
      titel: vorschau.blockiert
        ? 'Die Vorlage hat Fehler und kann nicht versendet werden.'
        : undefined,
      beiKlick: () => {
        ersetzen(
          inhaltBereich,
          bestaetigungsInhalt({
            empfaenger: vorschau.empfaenger,
            geschaetzteDauer: vorschau.geschaetzteDauer,
            senden: () => Kampagnen.senden(kampagne.id),
            abbrechen: () => uebersichtZeichnen(vorschau),
            nachErfolg: () => {
              schliessen();
              neuLaden();
            },
          }),
        );
      },
    });

    ersetzen(
      inhaltBereich,
      kopfMeldung,
      zahlen,
      vorschau.ohnePersoenlicheAnrede > 0
        ? meldung(
            'info',
            `${vorschau.ohnePersoenlicheAnrede} Empfänger bekommen „Sehr geehrte Damen und Herren", weil Anrede oder Nachname fehlen.`,
          )
        : null,
      el('h3', { text: 'So sieht die Nachricht aus' }),
      vorschauRahmen(vorschau.html),
      vorschau.befunde.length
        ? el('details.vertiefung', { open: vorschau.blockiert }, [
            el('summary', {
              text: `Prüfbericht: ${fehlerListe.length} Fehler, ${warnungen.length} Hinweise`,
            }),
            el('div', [befundeListe(vorschau.befunde)]),
          ])
        : null,
      el('div.knopf-reihe', [
        sendenKnopf,
        knopf('Vorlage im Editor öffnen', {
          beiKlick: () => {
            location.hash = `#/editor/${kampagne.vorlage_id}`;
          },
        }),
      ]),
    );
  }

  Kampagnen.vorschau(kampagne.id)
    .then((vorschau) => {
      if (vorschau.empfaenger === 0) {
        ersetzen(
          inhaltBereich,
          meldung('warnung', 'Diese Aussendung hat keine offenen Empfänger mehr.'),
        );
        return;
      }
      uebersichtZeichnen(vorschau);
    })
    .catch((fehler) =>
      ersetzen(
        inhaltBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Prüfung fehlgeschlagen'),
      ),
    );
}

/**
 * Rückfrage vor dem endgültigen Löschen eines Entwurfs.
 * @param {any} kampagne
 * @param {() => void} neuLaden
 */
function loeschenBestaetigenDialog(kampagne, neuLaden) {
  const fehlerBereich = el('div');
  const schliessen = dialog({
    titel: 'Entwurf löschen?',
    inhalt: [
      el('p', {
        text: `„${kampagne.name}" wird endgültig gelöscht. Das lässt sich nicht rückgängig machen.`,
      }),
      fehlerBereich,
    ],
    aktionen: [
      knopf('Abbrechen', { beiKlick: () => schliessen() }),
      knopf('Entwurf löschen', {
        art: 'gefahr',
        beiKlick: async () => {
          try {
            await Kampagnen.loeschen(kampagne.id);
            schliessen();
            neuLaden();
          } catch (fehler) {
            ersetzen(
              fehlerBereich,
              meldung('fehler', fehler instanceof Error ? fehler.message : 'Löschen fehlgeschlagen.'),
            );
          }
        },
      }),
    ],
  });
}

/**
 * Aktionen und Kennzeichen für die Kopfzeile eines Eintrags — abhängig vom
 * Status. Jede Aussendung soll von hier aus etwas Sinnvolles tun können,
 * statt nur dazuliegen.
 *
 * @param {any} kampagne
 * @param {() => void} neuLaden
 * @returns {Node[]}
 */
function statusAktionen(kampagne, neuLaden) {
  if (kampagne.status === 'laeuft') {
    return [
      knopf('Anhalten', {
        beiKlick: async () => {
          await Kampagnen.anhalten(kampagne.id);
          neuLaden();
        },
      }),
    ];
  }
  if (kampagne.status === 'entwurf') {
    return [
      zustand(kampagne),
      knopf('Prüfen und senden', {
        art: 'haupt',
        beiKlick: () => pruefenUndSendenDialog(kampagne, neuLaden),
      }),
      knopf('Löschen', {
        art: 'gefahr',
        beiKlick: () => loeschenBestaetigenDialog(kampagne, neuLaden),
      }),
    ];
  }
  if (kampagne.status === 'angehalten') {
    return [zustand(kampagne)];
  }
  return [zustand(kampagne)];
}

/**
 * Stellt die Prüfbefunde dar.
 * @param {{schwere: string, nachricht: string, hinweis?: string}[]} befunde
 * @returns {HTMLElement}
 */
function befundeListe(befunde) {
  return el(
    'ul.befund-liste',
    befunde.map((befund) =>
      el(`li.${befund.schwere}`, [
        el('strong', { text: befund.nachricht }),
        befund.hinweis ? el('span', { text: befund.hinweis }) : null,
      ]),
    ),
  );
}

/**
 * Vorschau der fertigen Nachricht in einem abgeschotteten Rahmen.
 *
 * srcdoc mit sandbox: das Vorschau-HTML wird angezeigt, kann aber weder
 * Skripte ausführen noch auf die Oberfläche zugreifen.
 *
 * @param {string} html
 * @returns {HTMLElement}
 */
function vorschauRahmen(html) {
  const rahmen = el('div.vorschau-rahmen');
  const iframe = el('iframe', {
    sandbox: '',
    srcdoc: html,
    title: 'Vorschau der Nachricht',
  });

  const umschalten = (mobil) => {
    rahmen.classList.toggle('mobil', mobil);
    rahmen.querySelectorAll('.vorschau-leiste .knopf').forEach((k, index) => {
      k.classList.toggle('haupt', index === (mobil ? 1 : 0));
    });
  };

  rahmen.append(
    el('div.vorschau-leiste', [
      el('span', { text: 'Ansicht:' }),
      knopf('Rechner', { art: 'haupt', beiKlick: () => umschalten(false) }),
      knopf('Telefon', { beiKlick: () => umschalten(true) }),
    ]),
    iframe,
  );
  return rahmen;
}

/**
 * @returns {import('../app.js').Ansicht}
 */
export function kampagnenAnsicht() {
  return {
    titel: 'Aussendungen',
    async zeichnen(behaelter) {
      const bereich = el('div');

      const laden = async () => {
        ersetzen(bereich, laedt());
        const kampagnen = await Kampagnen.liste();

        if (!kampagnen.length) {
          ersetzen(
            bereich,
            el('p.leer', { text: 'Noch keine Aussendung angelegt.' }),
          );
          return;
        }

        ersetzen(
          bereich,
          el(
            'div.auswahl',
            kampagnen.map((kampagne) => {
              const gesamt = kampagne.gesendet + kampagne.offen + kampagne.fehlgeschlagen;
              const grund = kampagne.status === 'fehler' ? abbruchgrund(kampagne) : null;
              const kopf = el('div.auswahl-eintrag-kopf', [
                el('span', [
                  el('span.haupttext', { text: kampagne.name }),
                  el('span.nebentext', {
                    text: `${kampagne.betreff} · ${kampagne.gesendet} von ${gesamt} versendet${
                      kampagne.fehlgeschlagen ? ` · ${kampagne.fehlgeschlagen} Fehler` : ''
                    } · ${zeitpunkt(kampagne.erstellt_am)}`,
                  }),
                ]),
                el('span.rechts', statusAktionen(kampagne, laden)),
              ]);

              // Aktionen, Kennzahlen und der Weiterweg nach einer Welle stehen
              // direkt in der Liste — jede Aussendung soll von hier aus etwas
              // Sinnvolles tun können, statt nur dazuliegen.
              /** @type {Node[]} */
              const zusatz = [];
              if (kampagne.status === 'fehler') {
                zusatz.push(
                  meldung(
                    'fehler',
                    grund
                      ? `Abbruchgrund: ${grund}`
                      : 'Der Versand wurde abgebrochen. Der genaue Grund liegt nur im Serverprotokoll vor.',
                  ),
                );
              }
              if (kampagne.status === 'laeuft') {
                zusatz.push(fortschrittsleiste(kampagne));
              }
              if (kampagne.status === 'angehalten') {
                zusatz.push(angehaltenFortsetzenBereich(kampagne, laden));
              }
              if (kampagne.status === 'welle_beendet') {
                zusatz.push(welleFortsetzenBereich(kampagne, laden));
              }
              if (KENNZAHLEN_STATUS.has(kampagne.status)) {
                zusatz.push(kennzahlenBereich(kampagne));
              }
              zusatz.push(detailsBereich(kampagne));

              return el('div.auswahl-eintrag.auswahl-eintrag--erweitert', [kopf, ...zusatz]);
            }),
          ),
        );
      };

      ersetzen(
        behaelter,
        el('div.bahn', [
          el('div.seiten-kopf', [
            el('div', [
              el('h1', { text: 'Aussendungen' }),
              el('p', { text: 'Alle Newsletter, die versendet wurden oder gerade laufen.' }),
            ]),
            knopf('Neue Aussendung', {
              art: 'haupt',
              beiKlick: () => {
                location.hash = '#/kampagnen/neu';
              },
            }),
          ]),
          karte({ inhalt: bereich }),
        ]),
      );

      await laden();

      // Läuft gerade eine Aussendung, aktualisiert sich die Liste von selbst.
      const takt = setInterval(async () => {
        if (!document.body.contains(bereich)) {
          clearInterval(takt);
          return;
        }
        const kampagnen = await Kampagnen.liste().catch(() => []);
        if (kampagnen.some((/** @type {any} */ k) => k.status === 'laeuft')) laden();
      }, 5000);
    },
  };
}

/**
 * Der geführte Ablauf.
 * @returns {import('../app.js').Ansicht}
 */
export function neueKampagneAnsicht() {
  return {
    titel: 'Neue Aussendung',
    async zeichnen(behaelter) {
      const [vorlagen, segmente] = await Promise.all([Vorlagen.liste(), Segmente.liste()]);

      /** @type {{ schritt: number, vorlageId: number|null, segmentId: number|null, name: string, betreff: string, kampagneId: number|null, vorschau: any }} */
      const stand = {
        schritt: 0,
        vorlageId: null,
        segmentId: null,
        name: '',
        betreff: '',
        kampagneId: null,
        vorschau: null,
      };

      const bereich = el('div');

      const zeichnen = () => {
        const inhalt = [schrittanzeige(SCHRITTE, stand.schritt)];
        if (stand.schritt === 0) inhalt.push(schrittVorlage());
        if (stand.schritt === 1) inhalt.push(schrittEmpfaenger());
        if (stand.schritt === 2) inhalt.push(schrittPruefen());
        if (stand.schritt === 3) inhalt.push(schrittSenden());
        ersetzen(bereich, ...inhalt);
      };

      // --- Schritt 1: Vorlage ---------------------------------------------

      function schrittVorlage() {
        if (!vorlagen.length) {
          return karte({
            titel: 'Es gibt noch keine Vorlage',
            inhalt: [
              el('p', {
                text: 'Für eine Aussendung brauchen Sie zuerst einen gestalteten Newsletter.',
              }),
              el('div.knopf-reihe', [
                knopf('Vorlage im Editor erstellen', {
                  art: 'haupt',
                  beiKlick: () => {
                    location.hash = '#/editor';
                  },
                }),
              ]),
            ],
          });
        }

        return karte({
          titel: 'Welche Vorlage soll versendet werden?',
          hinweis: 'Die Gestaltung des Newsletters. Sie können sie später noch prüfen.',
          inhalt: [
            el(
              'div.auswahl',
              vorlagen.map((vorlage) =>
                el(
                  `button.auswahl-eintrag${stand.vorlageId === vorlage.id ? '.gewaehlt' : ''}`,
                  {
                    type: 'button',
                    onclick: () => {
                      stand.vorlageId = vorlage.id;
                      if (!stand.betreff) stand.betreff = vorlage.betreff ?? '';
                      if (!stand.name) stand.name = vorlage.name;
                      stand.schritt = 1;
                      zeichnen();
                    },
                  },
                  [
                    el('span', [
                      el('span.haupttext', { text: vorlage.name }),
                      el('span.nebentext', {
                        text: `Zuletzt bearbeitet ${zeitpunkt(vorlage.geaendert_am)}`,
                      }),
                    ]),
                  ],
                ),
              ),
            ),
          ],
        });
      }

      // --- Schritt 2: Empfänger -------------------------------------------

      function schrittEmpfaenger() {
        const { feld: nameFeld, eingabe: nameEingabe } = eingabefeld({
          beschriftung: 'Name der Aussendung',
          wert: stand.name,
          hinweis: 'Nur für Ihre Übersicht — die Empfänger sehen ihn nicht.',
          beiAenderung: (wert) => {
            stand.name = wert;
          },
        });

        const { feld: betreffFeld, eingabe: betreffEingabe } = eingabefeld({
          beschriftung: 'Betreffzeile',
          wert: stand.betreff,
          platzhalter: 'z. B. Wichtige Fristen im Juli',
          hinweis:
            'Erscheint im Posteingang. Platzhalter sind erlaubt, z. B. {{ anrede }} für die persönliche Ansprache.',
          beiAenderung: (wert) => {
            stand.betreff = wert;
          },
        });

        const auswahl = el('div.auswahl', [
          el(
            `button.auswahl-eintrag${stand.segmentId === null ? '.gewaehlt' : ''}`,
            {
              type: 'button',
              onclick: () => {
                stand.segmentId = null;
                zeichnen();
              },
            },
            [
              el('span', [
                el('span.haupttext', { text: 'Alle Empfänger' }),
                el('span.nebentext', {
                  text: 'Jeder Kontakt mit dokumentierter Einwilligung, der sich nicht abgemeldet hat.',
                }),
              ]),
            ],
          ),
          ...segmente.map((segment) =>
            el(
              `button.auswahl-eintrag${stand.segmentId === segment.id ? '.gewaehlt' : ''}`,
              {
                type: 'button',
                onclick: () => {
                  stand.segmentId = segment.id;
                  zeichnen();
                },
              },
              [
                el('span', [
                  el('span.haupttext', { text: segment.name }),
                  el('span.nebentext', { text: `${segment.anzahl} Empfänger` }),
                ]),
              ],
            ),
          ),
        ]);

        const weiter = async () => {
          if (!stand.betreff.trim()) {
            nameEingabe.focus();
            ersetzen(
              meldungsBereich,
              meldung('warnung', 'Bitte geben Sie eine Betreffzeile an.'),
            );
            betreffEingabe.focus();
            return;
          }
          ersetzen(meldungsBereich, laedt('Empfängerliste wird zusammengestellt …'));
          try {
            const kampagne = await Kampagnen.anlegen({
              name: stand.name.trim() || 'Aussendung',
              vorlageId: /** @type {number} */ (stand.vorlageId),
              segmentId: stand.segmentId,
              betreff: stand.betreff.trim(),
            });
            stand.kampagneId = kampagne.id;
            stand.schritt = 2;
            zeichnen();
          } catch (fehler) {
            ersetzen(
              meldungsBereich,
              meldung('fehler', fehler instanceof Error ? fehler.message : 'Fehlgeschlagen'),
            );
          }
        };

        const meldungsBereich = el('div');

        return karte({
          titel: 'Wer soll den Newsletter bekommen?',
          inhalt: [
            auswahl,
            el('div.feld-reihe', [nameFeld, betreffFeld]),
            meldungsBereich,
            el('div.knopf-reihe', [
              knopf('Weiter zur Prüfung', { art: 'haupt', beiKlick: weiter }),
              knopf('Zurück', {
                art: 'still',
                beiKlick: () => {
                  stand.schritt = 0;
                  zeichnen();
                },
              }),
            ]),
          ],
        });
      }

      // --- Schritt 3: Prüfen ----------------------------------------------

      function schrittPruefen() {
        const bereichPruefung = el('div', [laedt('Nachricht wird zusammengesetzt …')]);

        (async () => {
          try {
            const vorschau = await Kampagnen.vorschau(/** @type {number} */ (stand.kampagneId));
            stand.vorschau = vorschau;

            const fehler = vorschau.befunde.filter(
              (/** @type {any} */ b) => b.schwere === 'fehler',
            );
            const warnungen = vorschau.befunde.filter(
              (/** @type {any} */ b) => b.schwere === 'warnung',
            );

            const kopfMeldung = vorschau.blockiert
              ? meldung(
                  'fehler',
                  `${fehler.length} Punkte verhindern den Versand. Bitte die Vorlage im Editor überarbeiten.`,
                )
              : meldung(
                  'erfolg',
                  'Die Nachricht ist versandfähig — sie wird in Outlook, Gmail und Apple Mail korrekt dargestellt.',
                );

            const zahlen = el('div.kacheln', [
              el('div.kachel', [
                el('div.zahl', { text: String(vorschau.empfaenger) }),
                el('div.beschriftung', { text: 'Empfänger' }),
              ]),
              el('div.kachel', [
                el('div.zahl', { text: vorschau.geschaetzteDauer }),
                el('div.beschriftung', { text: 'Voraussichtliche Dauer' }),
              ]),
              el('div.kachel', [
                el('div.zahl', { text: String(vorschau.ohnePersoenlicheAnrede) }),
                el('div.beschriftung', { text: 'ohne persönliche Anrede' }),
              ]),
            ]);

            const testBereich = el('div');
            const { feld: testFeld, eingabe: testEingabe } = eingabefeld({
              beschriftung: 'Probeversand an eigene Adressen',
              platzhalter: 'ihre.adresse@steuerservice.at, test@gmail.com, test@gmx.at',
              // Vorbelegt mit den zuletzt verwendeten Adressen: der Probeversand
              // ist ein wiederkehrender Schritt, und die Adressen bleiben
              // dieselben.
              wert: localStorage.getItem('probeadressen') ?? '',
              hinweis:
                'Mehrere Adressen mit Komma trennen. Sinnvoll ist je eine bei Gmail, Outlook.com und GMX — ' +
                'wie eine Aussendung dort tatsächlich ankommt, sieht man nur dort.',
            });

            /**
             * @param {string} roh
             * @returns {string[]}
             */
            const adressenLesen = (roh) =>
              roh
                .split(/[,;\s]+/)
                .map((teil) => teil.trim())
                .filter(Boolean);

            const testSenden = async () => {
              const adressen = adressenLesen(testEingabe.value);
              if (!adressen.length) return;

              localStorage.setItem('probeadressen', adressen.join(', '));
              ersetzen(
                testBereich,
                laedt(
                  adressen.length === 1
                    ? 'Testmail wird versendet …'
                    : `${adressen.length} Testmails werden versendet …`,
                ),
              );

              try {
                const ergebnis = await Kampagnen.test(
                  /** @type {number} */ (stand.kampagneId),
                  adressen,
                );

                /** @type {{an: string, erfolg: boolean, fehler?: string}[]} */
                const einzeln = ergebnis.ergebnisse ?? [
                  { an: adressen[0] ?? '', erfolg: ergebnis.erfolg, fehler: ergebnis.fehler },
                ];

                ersetzen(
                  testBereich,
                  meldung(
                    ergebnis.erfolg ? 'erfolg' : 'warnung',
                    ergebnis.erfolg
                      ? `${einzeln.length === 1 ? 'Testmail' : `${einzeln.length} Testmails`} versendet. Bitte jetzt in den Postfächern nachsehen.`
                      : 'Nicht alle Testmails konnten versendet werden.',
                  ),
                  el(
                    'div.auswahl',
                    einzeln.map((e) =>
                      el('div.auswahl-eintrag', [
                        el('span', [
                          el('span.haupttext', { text: e.an }),
                          e.fehler ? el('span.nebentext', { text: e.fehler }) : null,
                        ]),
                        el('span.rechts', [
                          kennzeichen(e.erfolg ? 'versendet' : 'fehlgeschlagen', e.erfolg ? 'gruen' : 'rot'),
                        ]),
                      ]),
                    ),
                  ),
                  // Die Nachschau ist der eigentliche Test. Ohne diese Liste
                  // bliebe es bei „versendet" — und das beweist nichts.
                  ergebnis.pruefliste?.length
                    ? karte({
                        titel: 'Was Sie jetzt im Postfach prüfen sollten',
                        inhalt: [
                          el(
                            'ul.pruefliste',
                            ergebnis.pruefliste.map((/** @type {string} */ punkt) =>
                              el('li', { text: punkt }),
                            ),
                          ),
                        ],
                      })
                    : null,
                );
              } catch (fehler) {
                ersetzen(
                  testBereich,
                  meldung(
                    'fehler',
                    fehler instanceof Error ? fehler.message : 'Testmail fehlgeschlagen',
                  ),
                );
              }
            };

            ersetzen(
              bereichPruefung,
              kopfMeldung,
              zahlen,
              vorschau.ohnePersoenlicheAnrede > 0
                ? meldung(
                    'info',
                    `${vorschau.ohnePersoenlicheAnrede} Empfänger bekommen „Sehr geehrte Damen und Herren", weil Anrede oder Nachname fehlen.`,
                  )
                : null,
              karte({
                titel: 'So sieht die Nachricht aus',
                hinweis: `Betreff: ${vorschau.betreff}`,
                inhalt: vorschauRahmen(vorschau.html),
              }),
              karte({
                titel: 'Probeversand',
                inhalt: [
                  testFeld,
                  el('div.knopf-reihe', [knopf('Probeversand starten', { beiKlick: testSenden })]),
                  testBereich,
                ],
              }),
              vorschau.befunde.length
                ? el('details.vertiefung', { open: vorschau.blockiert }, [
                    el('summary', {
                      text: `Prüfbericht: ${fehler.length} Fehler, ${warnungen.length} Hinweise`,
                    }),
                    el('div', [befundeListe(vorschau.befunde)]),
                  ])
                : null,
              el('div.knopf-reihe', [
                knopf('Weiter zum Versand', {
                  art: 'haupt',
                  deaktiviert: vorschau.blockiert || vorschau.empfaenger === 0,
                  titel: vorschau.blockiert
                    ? 'Die Vorlage hat Fehler und kann nicht versendet werden.'
                    : undefined,
                  beiKlick: () => {
                    stand.schritt = 3;
                    zeichnen();
                  },
                }),
                knopf('Vorlage im Editor öffnen', {
                  beiKlick: () => {
                    location.hash = `#/editor/${stand.vorlageId}`;
                  },
                }),
              ]),
            );
          } catch (fehler) {
            ersetzen(
              bereichPruefung,
              meldung('fehler', fehler instanceof Error ? fehler.message : 'Prüfung fehlgeschlagen'),
            );
          }
        })();

        return karte({
          titel: 'Prüfen, bevor es hinausgeht',
          hinweis:
            'Eine versendete Nachricht lässt sich nicht zurückholen. Diese Seite zeigt genau, was ankommt.',
          inhalt: bereichPruefung,
        });
      }

      // --- Schritt 4: Senden ----------------------------------------------

      function schrittSenden() {
        const bereichSenden = el('div');

        // Gestaffelter Start: bei der ersten Aussendung über einen neuen
        // Absender kennen Gmail, Outlook & Co. die Adresse noch nicht — eine
        // plötzlich große Menge ist selbst ein Verdachtsmoment. Eine kleine
        // erste Welle zeigt, wie die Nachricht tatsächlich ankommt, bevor
        // alle Empfänger sie bekommen haben.
        let gestaffelt = false;
        const teilmengeEingabe = /** @type {HTMLInputElement} */ (
          el('input.teilmenge-zahl', {
            type: 'number',
            min: '1',
            step: '1',
            value: '50',
            disabled: true,
            'aria-label': 'Anzahl Empfänger für die erste Welle',
          })
        );
        const teilmengeCheckbox = /** @type {HTMLInputElement} */ (
          el('input', {
            type: 'checkbox',
            onchange: () => {
              gestaffelt = teilmengeCheckbox.checked;
              teilmengeEingabe.disabled = !gestaffelt;
            },
          })
        );
        const teilmengeWahl = el('label.teilmenge-wahl', [
          teilmengeCheckbox,
          el('span.teilmenge-text', [
            el('span.haupttext', { text: 'Erst an eine Teilmenge senden' }),
            el('span.nebentext', {
              text:
                'Sinnvoll bei der ersten Aussendung über einen neuen Absender: die großen Anbieter kennen ' +
                'die Adresse noch nicht, und eine plötzlich große Menge ist selbst ein Verdachtsmoment.',
            }),
          ]),
          teilmengeEingabe,
          el('span', { text: 'Empfänger' }),
        ]);

        const wirklichSenden = async () => {
          const hoechstens = gestaffelt ? Number(teilmengeEingabe.value) : undefined;
          if (gestaffelt && (!Number.isFinite(hoechstens) || (hoechstens ?? 0) < 1)) {
            ersetzen(bereichSenden, meldung('warnung', 'Bitte eine gültige Anzahl Empfänger angeben.'));
            return;
          }

          const schliessen = dialog({
            titel: 'Aussendung starten?',
            inhalt: [
              el('p', {
                text: hoechstens
                  ? `Die ersten ${hoechstens} von ${stand.vorschau.empfaenger} Empfängern werden jetzt angeschrieben. Das lässt sich nicht rückgängig machen.`
                  : `Die Nachricht geht an ${stand.vorschau.empfaenger} Empfänger. Das lässt sich nicht rückgängig machen.`,
              }),
              el('p', {
                text: `Voraussichtliche Dauer: ${stand.vorschau.geschaetzteDauer}. Sie können den Versand jederzeit anhalten.`,
              }),
            ],
            aktionen: [
              knopf('Abbrechen', { beiKlick: () => schliessen() }),
              knopf('Jetzt senden', {
                art: 'haupt',
                beiKlick: async () => {
                  schliessen();
                  ersetzen(bereichSenden, laedt('Aussendung wird gestartet …'));
                  try {
                    await Kampagnen.senden(
                      /** @type {number} */ (stand.kampagneId),
                      hoechstens ? { hoechstens } : undefined,
                    );
                    ersetzen(
                      bereichSenden,
                      meldung(
                        'erfolg',
                        hoechstens
                          ? `Die erste Welle von ${hoechstens} Nachrichten läuft. Danach sehen Sie bei den Aussendungen, wie viele Empfänger noch offen sind, und können fortsetzen.`
                          : 'Die Aussendung läuft. Sie können das Fenster schließen — der Versand läuft auf dem Server weiter.',
                      ),
                      el('div.knopf-reihe', [
                        knopf('Fortschritt ansehen', {
                          art: 'haupt',
                          beiKlick: () => {
                            location.hash = '#/kampagnen';
                          },
                        }),
                      ]),
                    );
                  } catch (fehler) {
                    const text =
                      fehler instanceof Error ? fehler.message : 'Versand fehlgeschlagen';
                    ersetzen(
                      bereichSenden,
                      meldung('fehler', text),
                      // @ts-ignore - details sind optional
                      fehler?.details ? befundeListe(fehler.details) : null,
                    );
                  }
                },
              }),
            ],
          });
        };

        return karte({
          titel: 'Bereit zum Versand',
          inhalt: [
            el('p', {
              text: `„${stand.name}" geht an ${stand.vorschau?.empfaenger ?? 0} Empfänger, versendet über ${stand.vorschau?.provider ?? 'den eingestellten Weg'}.`,
            }),
            teilmengeWahl,
            el('div.knopf-reihe', [
              knopf('Aussendung starten', { art: 'haupt', beiKlick: wirklichSenden }),
              knopf('Zurück zur Prüfung', {
                art: 'still',
                beiKlick: () => {
                  stand.schritt = 2;
                  zeichnen();
                },
              }),
            ]),
            bereichSenden,
          ],
        });
      }

      ersetzen(
        behaelter,
        el('div.bahn', [
          el('div.seiten-kopf', [
            el('div', [
              el('h1', { text: 'Neue Aussendung' }),
              el('p', { text: 'In vier Schritten zum versendeten Newsletter.' }),
            ]),
          ]),
          bereich,
        ]),
      );

      zeichnen();
    },
  };
}
